import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  type Config,
  getDefaultLoyaltyConfig,
  getDefaultLeaderboardSpecialPrize
} from "kadow-jetpack-model";
import { type FormikFormChildProps } from "../FormikForm/FormikFormWrapperForJetpackConfig";
import { updateJetpackConfigThunk } from "../../../../redux/jetpackConfig";
import { KC_Sizes } from "../../../../designsystem/ds_commons";
import { KC_ButtonSize } from "../../../../designsystem/KC_Button/KC_Button";
import KC_Separator from "../../../../designsystem/KC_Separator/KC_Separator";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { tt } from "../../../../utils/i18n/i18n";
import { configAnimation } from "../../../../utils/routes/routes";
import { buildPathWithTrackingId } from "../../../../utils/routing/routingUtils";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import MLPreview from "../MLSelector/components/MLPreview";
import useGLM from "../../../../utils/hooks/useGLM";
import { JetpackConfigAPI } from "../../api/JetpackConfigAPI";
import KC_Padder from "../../../../designsystem/KC_Padder/KC_Padder";
import "../Loyalty/styles.scss";
import "./styles.scss";
import PrizesSection from "./sections/PrizesSection";
import LoyaltyTiersSection from "./sections/LoyaltyTiersSection";
import CustomerOfMonthSection from "./sections/CustomerOfMonthSection";

const PrizesAndLoyaltyConfigurator: React.FC<FormikFormChildProps> = (
  props
) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const { GLMList } = useGLM(
    accountToDisplay?.token,
    JetpackConfigAPI.getCommonML
  );

  const hasLoyalty = useMemo(() => {
    return accountToDisplay?.saasFeatures?.lty;
  }, [accountToDisplay]);

  const persistLtyPatch = async (ltyPatch: Record<string, unknown>) => {
    if (!accountToDisplay?.trackingID || !accountToDisplay?.token) {
      return;
    }
    const updatedConfig = {
      ...props.config,
      lty: { ...(props.config.lty ?? {}), ...ltyPatch }
    } as Config;
    await dispatch(
      updateJetpackConfigThunk(
        updatedConfig,
        accountToDisplay.trackingID,
        accountToDisplay.token
      ) as any
    );
  };

  const handleToggleTiersEnabled = async () => {
    if (!accountToDisplay?.token) {
      return;
    }

    const isEnabled = props.config.lty?.enabled ?? false;
    const newEnabled = !isEnabled;

    if (!newEnabled && !confirm(tt("loyalty_disable_confirmation"))) {
      return;
    }

    if (newEnabled) {
      const existingTiers = props.config.lty?.tiers;
      const tiers = existingTiers?.length
        ? existingTiers
        : getDefaultLoyaltyConfig(true).tiers;

      props.setFieldValue("lty.enabled", true);
      props.setFieldValue("lty.tiers", tiers);
      await persistLtyPatch({ enabled: true, tiers });
    } else {
      props.setFieldValue("lty.enabled", false);
      await persistLtyPatch({ enabled: false });
    }
  };

  const handleToggleLeaderboardEnabled = async () => {
    if (!accountToDisplay?.token) {
      return;
    }

    const isEnabled = props.config.lty?.leaderboardEnabled ?? false;
    const newEnabled = !isEnabled;

    if (
      !newEnabled &&
      !confirm(tt("loyalty_leaderboard_disable_confirmation"))
    ) {
      return;
    }

    if (newEnabled) {
      const existingPrize = props.config.lty?.leaderboardSpecialPrize;
      const leaderboardSpecialPrize =
        existingPrize ?? getDefaultLeaderboardSpecialPrize();
      props.setFieldValue("lty.leaderboardEnabled", true);
      props.setFieldValue(
        "lty.leaderboardSpecialPrize",
        leaderboardSpecialPrize
      );
      await persistLtyPatch({
        leaderboardEnabled: true,
        leaderboardSpecialPrize
      });
    } else {
      props.setFieldValue("lty.leaderboardEnabled", false);
      await persistLtyPatch({ leaderboardEnabled: false });
    }
  };

  const navigateToPrizesSetting = () => {
    history.push(
      buildPathWithTrackingId(
        `${configAnimation}/prizes/setting`,
        accountToDisplay?.trackingID
      )
    );
  };

  const navigateToTiers = () => {
    history.push(
      buildPathWithTrackingId(
        `${configAnimation}/prizes/gifts/tiers`,
        accountToDisplay?.trackingID
      ),
      { draftConfig: props.config }
    );
  };

  const navigateToLeaderboard = () => {
    history.push(
      buildPathWithTrackingId(
        `${configAnimation}/prizes/gifts/leaderboard`,
        accountToDisplay?.trackingID
      ),
      { draftConfig: props.config }
    );
  };

  const choosePrizesButtonProps = useMemo(() => {
    return {
      size: KC_ButtonSize.Large,
      text: tt("prizes_configurator_choose_my_prizes"),
      isPrimary: true,
      showIconOnTheRight: true,
      icon: faArrowRight,
      onClick: navigateToPrizesSetting,
      extraStyle: { width: "unset !important" as const }
    };
  }, [navigateToPrizesSetting]);

  return (
    <div className="dashboard-v2 loyalty-config-editor">
      {/*<MobileDashboardHeader*/}
      {/*  title={tt("prizes_configurator_my_prizes")}*/}
      {/*  titleButton={*/}
      {/*    <KC_Button id="choose-my-prizes" {...choosePrizesButtonProps} />*/}
      {/*  }*/}
      {/*  description={tt("prizes_configurator_text_about_prizes")}*/}
      {/*/>*/}
      <div className="px-4 pt-5 pb-5 d-flex flex-column st-gap prizes-config-sections">
        <PrizesSection
          config={props.config}
          onChoosePrizesClick={navigateToPrizesSetting}
        />

        {!!hasLoyalty && (
          <>
            <KC_Separator size={KC_Sizes.m} />

            <LoyaltyTiersSection
              config={props.config}
              onToggleTiersEnabled={handleToggleTiersEnabled}
              onChooseTiersClick={navigateToTiers}
            />

            {!!accountToDisplay?.saasFeatures?.ltycm && (
              <>
                <KC_Separator size={KC_Sizes.m} />

                <CustomerOfMonthSection
                  config={props.config}
                  onToggleLeaderboardEnabled={handleToggleLeaderboardEnabled}
                  onChooseLeaderboardClick={navigateToLeaderboard}
                />
              </>
            )}
          </>
        )}

        <KC_Separator size={KC_Sizes.m} />

        <MLPreview
          glmList={GLMList}
          config={props.config}
          setFieldValue={props.setFieldValue}
        />
      </div>
      <KC_Separator size={KC_Sizes.sm} className="d-lg-none" />
      <KC_Padder className="d-none d-lg-flex" />
    </div>
  );
};

export default PrizesAndLoyaltyConfigurator;
